<template>
    <el-dialog
        :visible="visible"
        :append-to-body="true"
        title="Import Products"
        @close="$emit('close')"
    >
        <div class="products-import">
            <div v-if="!selectedSupplierId">
                <supplier-search
                    :selected-supplier="selectedSupplier"
                    @selected:supplier="handleSelectSupplier"
                />
            </div>
            <el-alert
                v-if="invalidFields.length > 0 && products.length === 0"
                class="product-alert"
                title="Invalid Columns"
                type="warning"
            >
                There are invalid columns which will not be imported. See
                example CSV for the correct format.
                <ul>
                    <li
                        v-for="(invalidField, idx) of invalidFields"
                        :key="`invalidField-${idx}`"
                    >
                        {{ invalidField }}
                    </li>
                </ul>
            </el-alert>
            <el-upload
                v-if="selectedSupplierId && data.length === 0"
                :on-change="fileAdded"
                :auto-upload="false"
                :on-remove="fileRemoved"
                :multiple="false"
                :limit="1"
                :drag="true"
                action=""
                class="csv-importer"
            >
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">
                    Drop file here or <em>click to upload</em>
                </div>
                <div slot="tip" class="el-upload__tip">
                    * csv files with
                    <a
                        href="/example-global-products-import.csv"
                        target="_blank"
                        @click.native="$event.stopPropagation()"
                        >example csv</a
                    >
                    format
                </div>
            </el-upload>
            <div
                v-if="data.length > 0 && products.length === 0"
                class="csv-importer"
            >
                <el-button-group>
                    <el-button
                        :disabled="importing"
                        :icon="`${importing ? 'el-icon-loading' : ''}`"
                        size="small"
                        type="primary"
                        @click="importProducts"
                        >Import Products</el-button
                    >
                    <el-button size="small" @click="cancelImport">
                        Cancel
                    </el-button>
                </el-button-group>
                <div class="import-info">
                    Import {{ data.length }} of {{ total }} Products.
                    <div v-if="data.length !== total">
                        <small>
                            {{ total - data.length }} Products can not be
                            imported due to incorrect or missing fields.
                        </small>
                    </div>
                </div>
            </div>
        </div>
    </el-dialog>
</template>
<script>
import { ProductService } from "Services/ProductService";
import SupplierSearch from "Components/SupplierSearch";

export default {
    components: {
        SupplierSearch,
    },
    props: {
        supplierId: {
            type: String,
            required: true,
        },
        visible: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            selectedSupplier: null,
            selectedProduct: null,
            importing: false,
            total: 0,
            data: [],
            invalidFields: [],
            products: [],
            newProducts: 0,
            validFields: [
                {
                    field: "code",
                    alt: ["product_code"],
                    required: true,
                    type: String,
                },
                {
                    field: "name",
                    alt: ["product_name"],
                    required: false,
                    type: String,
                },
                {
                    field: "unitOfMeasurement",
                    alt: ["uom", "unit_of_measurement"],
                    required: false,
                    type: ["L", "ml", "kg", "g"],
                },
                {
                    field: "salePricePerUnit",
                    alt: ["sale_price_per_unit", "saleprice"],
                    required: false,
                    type: Number,
                },
                {
                    field: "isSellable",
                    alt: ["sellable", "forsale", "for_sale", "is_sellable"],
                    required: false,
                    type: Boolean,
                },
                {
                    field: "isFavourite",
                    alt: ["favourte", "is_favourite"],
                    required: false,
                    type: Boolean,
                },

                {
                    field: "brand",
                    alt: [],
                    required: false,
                    type: String,
                },
                {
                    field: "type",
                    alt: [],
                    required: false,
                    type: ["UNIT", "CARTON", "PACKAGE", "QUANTITY"],
                },
                {
                    field: "unitType",
                    alt: ["unit_type"],
                    required: false,
                    type: String, // grab list of unit types from somewhere },
                },
                {
                    field: "unitSize",
                    alt: ["unit_size", "size"],
                    required: false,
                    type: Number,
                },
                {
                    field: "groups",
                    alt: ["carton_size", "cartonsize"],
                    required: false,
                    type: "GROUP_TYPE",
                },
                {
                    field: "cartonPrice",
                    alt: ["carton_price", "cartonprice"],
                    required: false,
                    type: Number,
                },
                {
                    field: "unitPrice",
                    alt: ["unit_price", "price"],
                    required: false,
                    type: Number,
                },
                {
                    field: "wet",
                    alt: [],
                    required: false,
                    type: Boolean,
                },
                {
                    field: "taxMethod",
                    alt: ["tax_method", "tax"],
                    required: false,
                    type: ["included", "excluded", "none"],
                },
            ],
        };
    },
    computed: {
        selectedSupplierId() {
            if (this.selectedSupplier) {
                return this.selectedSupplier._id;
            }

            if (
                this.supplierId &&
                this.supplier !== "" &&
                this.supplierId !== "-"
            ) {
                return this.supplierId;
            }

            return null;
        },
    },
    methods: {
        handleSelectSupplier(selectedSupplier) {
            this.selectedSupplier = selectedSupplier;
        },
        editProduct(product) {
            this.selectedProduct = { ...product };
        },
        fileAdded(file) {
            const reader = new FileReader();
            reader.onload = event => {
                const data = event.target.result
                    .split(/\r?\n/)
                    .map(row => row.split(","));

                const header = data.shift();

                const headerFields = header.map(col => this._findField(col));

                this.invalidFields = header.filter(
                    (col, idx) => !headerFields[idx]
                );

                this.total = data.length;
                this.data = data
                    .map(row => {
                        const cols = {};
                        headerFields.forEach((field, idx) => {
                            if (field) {
                                cols[field.field] = this._transformField(
                                    field,
                                    row[idx]
                                );
                            }
                        });

                        return cols;
                    })
                    // Remove rows which don't have the required fields
                    .filter(row => {
                        let isValid = true;
                        this.validFields
                            .filter(field => field.required)
                            .forEach(field => {
                                if (!row[field.field]) isValid = false;
                            });

                        return isValid;
                    });

                // Remove duplicates
                this.data = Array.from(new Set(this.data.map(a => a.code))).map(
                    code => {
                        return this.data.find(a => a.code === code);
                    }
                );

                this.data = this.data.map(product => {
                    // Do some price calculations
                    if (product.type && product.type === "CARTON") {
                        if (product.cartonPrice && product.groups) {
                            product.unitPrice =
                                product.cartonPrice /
                                product.groups[0].quantity;
                        }
                    }

                    return product;
                });
            };

            reader.readAsText(file.raw);
        },

        fileRemoved() {
            this.products = [];
            this.data = [];
            this.invalidFields = [];
        },

        async importProducts() {
            this.importing = true;

            const {
                data: supplierProducts,
            } = await ProductService.getBySupplier(this.selectedSupplierId);

            const products = this.data.map(row => {
                return this._existingProduct(row, supplierProducts);
            });

            this.products = products;
            this.newProducts = this.products.filter(
                product => !product._id
            ).length;

            this.products = await ProductService.importProductsForSupplier(
                this.selectedSupplierId,
                this.products
            );
            this.importing = false;

            this.$notify({
                title: "Success",
                message: `Successfully imported ${
                    this.products.length
                } products ( ${this.newProducts} new )`,
                type: "success",
            });
            this.$emit("close");
            this.$emit("upload:complete");
        },

        cancelImport() {
            this.products = [];
            this.data = [];
            this.invalidFields = [];
        },

        closeProductForm() {
            this.selectedProduct = null;
        },

        closeModal() {
            this.$emit("products:updated", this.products);
            this.$emit("close");
        },

        async handleSaveProduct(product) {
            // const updatedProduct = await ProductService.updateProduct(
            // this.selectedStore._id,
            // this.supplier._id,
            // product._id,
            // product
            // );
            // this.products = this.$_immutableCollection_update(
            // this.products,
            // updatedProduct,
            // SupplierProduct
            // );
            // this.closeProductForm();
        },

        confirmDeleteProduct(productToDelete) {
            this.$confirm(
                `Delete product "${productToDelete.name}"?`,
                "Are you sure?",
                {
                    confirmButtonText: "Yes",
                    cancelButtonText: "No",
                    customClass: "confirm-message-box",
                    cancelButtonClass: "cancel-button viability-button",
                    confirmButtonClass: "confirm-button viability-button",
                    center: true,
                }
            )
                .then(async () => {
                    await this.deleteProduct(productToDelete);
                })
                .catch(() => {});
        },

        async deleteProduct(productToDelete) {
            // const deletedProduct = await ProductService.deleteProduct(
            // this.selectedStore._id,
            // this.supplier._id,
            // productToDelete._id
            // );
            // this.products = this.$_immutableCollection_deleteById(
            // this.products,
            // deletedProduct._id
            // );
        },

        _findField(name) {
            return this.validFields.find(field => {
                if (name.toLowerCase() === field.field.toLowerCase())
                    return true;
                if (field.alt.indexOf(name.toLowerCase()) > -1) return true;
                return false;
            });
        },

        _validField(field, data) {
            if (field.type === Boolean) {
                const validBool = [
                    "true",
                    "false",
                    "y",
                    "n",
                    "yes",
                    "no",
                    true,
                    false,
                ];

                if (validBool.indexOf(data) > -1) return true;
                return false;
            }

            if (field.type === String) {
                if (field.required && data === "") {
                    return false;
                }
                return true;
            }

            if (field.type === Number) {
                if (!field.required && data === "") return true;
                if (!isNaN(data)) return true;
                return false;
            }

            if (Array.isArray(field.type)) {
                if (field.type.indexOf(data) > -1) return true;
                return false;
            }
            return false;
        },
        _validateRow(row) {},

        _transformField(field, data) {
            if (field.type === Boolean) {
                if (
                    data === true ||
                    data === "true" ||
                    data === "y" ||
                    data === "yes"
                ) {
                    return true;
                }
                return false;
            }

            if (field.type === Number) {
                if (field.required && isNaN(data)) return 0;
                if (isNaN(data)) return null;
                return +data;
            }

            if (Array.isArray(field.type)) {
                if (!this._validField(field, data)) {
                    return field.default && "";
                }
                return data;
            }

            if (field.type === "GROUP_TYPE") {
                return [{ depth: 0, quantity: +data, type: "UNIT" }];
            }
            return data;
        },

        // Checks if any existing Product exist
        _existingProduct(data, existingProducts) {
            if (existingProducts) {
                const existing = existingProducts.find(
                    existing => existing.code === data.code
                );

                if (!existing) return data;

                // Merge data, overwrite certain fields with existing fields
                return {
                    ...existing,
                    ...data,
                    name: existing.name,
                };
            }
            return data;
        },

        // Check if any existing global products exist
        _existingGlobalProduct(product, existingGlobalProducts) {
            if (product._id !== "") return product;

            const existing = existingGlobalProducts.find(
                existing => existing.code === product.code
            );

            if (!existing) return product;

            return {
                ...existing,
                ...product,
                name: existing.name,
                _id: "",
                globalProductId: existing._id,
            };
        },
    },
};
</script>
<style scoped lang="scss">
.products-import {
    min-height: 400px;
    height: 100%;
    padding-bottom: 100px;
    overflow: hidden;
}

.import-info {
    text-align: center;
    font-size: 14px;
    color: grey;
    padding: 10px;
    small {
        font-size: 12px;
        color: grey;
    }
}
/deep/ .csv-importer {
    width: 100%;
    height: 500px;
    background: lighten(#d4d4d4, 15%);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed lighten(grey, 20%);
    flex-direction: column;
    .el-upload__tip {
        position: absolute;
        top: 50%;
        margin-top: 50px;
    }
    .el-upload.el-upload--text {
        border: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .el-upload-dragger {
        border: none;
        background: none;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
    }
}
.product-import-form__footer {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    display: flex;
    justify-content: flex-end;

    & > div {
        margin-right: 10px;

        &:last-child {
            margin-right: 0;
        }
    }
}

/deep/ .products-table-view {
    height: 100%;
    padding-bottom: 50px;

    text-align: center;

    .products-table {
        overflow: scroll;
    }
}
</style>
